import React, { createContext, useMemo } from 'react'

import { ThemeConfig, defaultTheme, themes } from './themes'

export type ThemeContextValue = {
  theme: ThemeConfig
  isOrganizer: boolean
  organizerSeoName?: string
}

// eslint-disable-next-line react/only-export-components
export const ThemeContext = createContext<ThemeContextValue>({
  theme: defaultTheme,
  isOrganizer: false
})

type Props = {
  organizerSeoName?: string | null
  children: React.ReactNode
}

export default function ThemeProvider({ organizerSeoName, children }: Props) {
  const theme = (organizerSeoName && themes[organizerSeoName.toLowerCase()]) || defaultTheme

  const cssVariables = useMemo(
    () =>
      ({
        '--theme-primary': theme.colors.primary,
        '--theme-primary-hover': theme.colors.primaryHover,
        '--theme-primary-light': theme.colors.primaryLight,
        '--theme-primary-border': theme.colors.primaryBorder,
        '--theme-primary-text': theme.colors.primaryText,
        '--theme-accent': theme.colors.accent,
        '--theme-accent-light': theme.colors.accentLight,
        '--theme-button': theme.colors.button,
        '--theme-button-hover': theme.colors.buttonHover,
        '--theme-font-heading': theme.fonts.heading,
        '--theme-font-body': theme.fonts.body,
        '--theme-font-body-weight': theme.fonts.bodyWeight,
        '--theme-radius-button': theme.borderRadius.button,
        '--theme-radius-card': theme.borderRadius.card
      }) as React.CSSProperties,
    [theme]
  )

  const contextValue = useMemo(
    () => ({
      theme,
      isOrganizer: !!organizerSeoName,
      organizerSeoName: organizerSeoName || undefined
    }),
    [theme, organizerSeoName]
  )

  return (
    <ThemeContext.Provider value={contextValue}>
      <div style={cssVariables}>{children}</div>
    </ThemeContext.Provider>
  )
}
